import classes from './index.less';
import styles from './index.less';
import React, {ReactNode, useCallback, useEffect, useRef} from 'react';
import {Drawer as AntDesignDrawer, DrawerProps as AntDesignDrawerProps} from 'antd';
import {CloseCircleOutlined} from '@ant-design/icons';
import classNames from 'classnames';
import {getMaxZIndexInDrawers} from '@/utils/dom';
import {useIsMobile} from '@/hooks/useResponsive';
import {useIntl} from 'umi';
import {getIntlTranslation} from '@/utils/intl';

/** Drag distance past which releasing the sheet dismisses it instead of snapping back. */
const SHEET_DISMISS_DISTANCE = 120;
/** Matches the snap-back transition in index.less. */
const SHEET_SNAP_MS = 200;
/**
 * Drives the drag phase styling in index.less. An attribute rather than a class: rc-motion
 * rewrites this wrapper's className on every motion phase, so a class added here is dropped the
 * moment the drawer closes — React leaves attributes it never set alone.
 */
const SHEET_PHASE_ATTR = 'data-sheet-phase';
/** antd's panel motion (motionDurationSlow = 300ms) plus slack, after which the offset resets. */
const SHEET_CLOSE_MS = 350;

type SheetDragHandlers = {
  onTouchStart: (event: React.TouchEvent) => void;
  onTouchMove: (event: React.TouchEvent) => void;
  onTouchEnd: (event: React.TouchEvent) => void;
  onTouchCancel: () => void;
};

type DrawerProps = AntDesignDrawerProps & {
  open?: boolean;
  closable?: boolean;
  hideCloseIcon?: boolean;
  title: string | ReactNode;
  drawerHeaderClassName?: string;
  drawerBodyClassName?: string;
  children?: ReactNode;
  /**
   * A number is pixels; a string is passed through as CSS, so a drawer can size
   * itself against the viewport (`calc(100vw - 100px)`) and stay right on
   * resize. The `xs` breakpoint still overrides both with a full-width sheet.
   */
  width?: number | string;
  toggledAt?: number;
  onOpenChange: (open: boolean) => void;
};


/**
 * The drawer's close control.
 *
 * A `<button>` with a name, not the bare `<a onClick>` this used to be: an anchor
 * without an `href` is not focusable and exposes no role, and the icon's own
 * `aria-label` is "close-circle" — so the only way out of a drawer could not be
 * reached or understood without a mouse. AntD's own labelled close button is hidden
 * by this header, which made this the only exit.
 */
const DrawerCloseButton: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const intl = useIntl();
  return (
    <button
      type="button"
      className={classes.closeButton}
      aria-label={getIntlTranslation(intl, {
        id: 'common.actions.close',
        defaultMessage: 'Close',
      })}
      onClick={onClose}
    >
      <CloseCircleOutlined />
    </button>
  );
};

const DrawerHeader = ({
                        title,
                        toggle,
                        className,
                        hideCloseIcon,
                        dragHandlers,
                      }: {
  title: string | ReactNode;
  toggle: (open: boolean) => void;
  className: string;
  hideCloseIcon?: boolean;
  // On phones the header is a drag surface too — a 24px grabber is a hard target to hit.
  // Taps still reach the close icon: the gesture never calls preventDefault.
  dragHandlers?: SheetDragHandlers;
}) => (
  <div className={classNames(classes.drawerHeader, className)} {...dragHandlers}>
    <span>{title}</span>
    {!hideCloseIcon && <DrawerCloseButton onClose={() => toggle(false)} />}
  </div>
);

const MyDrawer: React.FC<DrawerProps> = ({
                                           open,
                                           closable = true,
                                           width = 450,
                                           onOpenChange,
                                           title,
                                           children,
                                           drawerHeaderClassName,
                                           drawerBodyClassName,
                                           hideCloseIcon,
                                           toggledAt,
                                           ...restProps
                                         }: DrawerProps) => {
  const isMobile = useIsMobile();
  const dragStartYRef = useRef<number | null>(null);

  const [zIndex, setZIndex] = React.useState(null);
  const instanceClassName = React.useMemo(() => `drawer-instance-${Math.random().toString(36).substring(2, 9)}`, []);
  const drawerWidth = isMobile ? '100%' : width;
  const placement = isMobile ? 'bottom' : 'right';

  // Drag-to-dismiss. The offset is written straight onto the wrapper as a CSS variable so the
  // gesture stays on the compositor instead of re-rendering the whole drawer on every move.
  // Phones fire touchmove faster than they paint, so writes are coalesced into one per frame
  // and the wrapper is resolved once per gesture instead of on every event.
  const sheetWrapperRef = useRef<HTMLElement | null>(null);
  const dragOffsetRef = useRef(0);
  const dragFrameRef = useRef<number | null>(null);
  const settleTimeoutRef = useRef<number | null>(null);

  const clearSettle = () => {
    if (settleTimeoutRef.current === null) return;
    clearTimeout(settleTimeoutRef.current);
    settleTimeoutRef.current = null;
  };

  const flushDragOffset = useCallback(() => {
    dragFrameRef.current = null;
    sheetWrapperRef.current?.style.setProperty('--sheet-drag-offset', `${dragOffsetRef.current}px`);
  }, []);

  const handleDragStart = useCallback((event: React.TouchEvent) => {
    clearSettle();
    dragStartYRef.current = event.touches[0].clientY;
    const wrapper = (event.currentTarget as HTMLElement).closest(
      '.ant-drawer-content-wrapper',
    ) as HTMLElement | null;
    sheetWrapperRef.current = wrapper;
    wrapper?.setAttribute(SHEET_PHASE_ATTR, 'dragging');
  }, []);

  const handleDragMove = useCallback(
    (event: React.TouchEvent) => {
      if (dragStartYRef.current === null) return;
      dragOffsetRef.current = Math.max(0, event.touches[0].clientY - dragStartYRef.current);
      if (dragFrameRef.current === null)
        dragFrameRef.current = requestAnimationFrame(flushDragOffset);
    },
    [flushDragOffset],
  );

  const endDrag = useCallback(
    (dismiss: boolean) => {
      dragStartYRef.current = null;
      if (dragFrameRef.current !== null) {
        cancelAnimationFrame(dragFrameRef.current);
        dragFrameRef.current = null;
      }
      const wrapper = sheetWrapperRef.current;
      sheetWrapperRef.current = null;
      if (!wrapper) {
        if (dismiss) onOpenChange(false);
        return;
      }

      if (dismiss) {
        // Keep the offset the finger left behind: antd's leave animation carries the sheet on
        // from there instead of snapping it back up first. Reset once that motion is over.
        wrapper.setAttribute(SHEET_PHASE_ATTR, 'settling');
        onOpenChange(false);
        settleTimeoutRef.current = window.setTimeout(() => {
          settleTimeoutRef.current = null;
          wrapper.removeAttribute(SHEET_PHASE_ATTR);
          wrapper.style.setProperty('--sheet-drag-offset', '0px');
        }, SHEET_CLOSE_MS);
        return;
      }

      wrapper.setAttribute(SHEET_PHASE_ATTR, 'snapping');
      wrapper.style.setProperty('--sheet-drag-offset', '0px');
      settleTimeoutRef.current = window.setTimeout(() => {
        settleTimeoutRef.current = null;
        wrapper.removeAttribute(SHEET_PHASE_ATTR);
      }, SHEET_SNAP_MS);
    },
    [onOpenChange],
  );

  const handleDragEnd = useCallback(
    (event: React.TouchEvent) => {
      if (dragStartYRef.current === null) return;
      const endY = event.changedTouches[0]?.clientY ?? dragStartYRef.current;
      endDrag(endY - dragStartYRef.current > SHEET_DISMISS_DISTANCE);
    },
    [endDrag],
  );

  // An interrupted gesture (system gesture, incoming call) still carries a touch point, so it
  // must reset explicitly instead of falling through to the dismiss check.
  const handleDragCancel = useCallback(() => {
    if (dragStartYRef.current === null) return;
    endDrag(false);
  }, [endDrag]);

  useEffect(
    () => () => {
      if (dragFrameRef.current !== null) cancelAnimationFrame(dragFrameRef.current);
      clearSettle();
    },
    [],
  );

  const dragHandlers: SheetDragHandlers | undefined = isMobile
    ? {
        onTouchStart: handleDragStart,
        onTouchMove: handleDragMove,
        onTouchEnd: handleDragEnd,
        onTouchCancel: handleDragCancel,
      }
    : undefined;

  useEffect(() => {
    if (open) setZIndex(Math.max(getMaxZIndexInDrawers(instanceClassName).maxZIndex, 1010) + 3);

  }, [open, instanceClassName]);

  useEffect(() => {
    if (!open)
      return;
    setZIndex(Math.max(getMaxZIndexInDrawers(instanceClassName).maxZIndex, 1010) + 3);
  }, [toggledAt, instanceClassName]);

  return (
    <div className={styles.drawerContainer}>
      <AntDesignDrawer
        closable={closable}
        width={drawerWidth}
        height={isMobile ? '100dvh' : '100%'}
        open={open}
        onClose={() => onOpenChange(false)}
        closeIcon={false}
        placement={placement}
        // Callers may override (e.g. destroyOnHidden={false} + forceRender for filter
        // panels that must initialize on page mount, not on first open).
        destroyOnHidden
        {...restProps}
        rootClassName={classNames(styles.antDrawerRoot, instanceClassName, {
          [styles.mobileSheetRoot]: isMobile,
        })}
        rootStyle={{
          zIndex: zIndex,
          '--calculated-z-index': zIndex,
        }}
        styles={{
          content: {
            ...(restProps?.styles || {}),
          },
        }}
      >
        {isMobile && (
          <div className={classes.sheetGrabberArea} {...dragHandlers} aria-hidden>
            <span className={classes.sheetGrabber} />
          </div>
        )}
        <DrawerHeader
          hideCloseIcon={hideCloseIcon}
          className={drawerHeaderClassName}
          title={title}
          toggle={onOpenChange}
          dragHandlers={dragHandlers}
        />
        <div className={classNames(classes.drawerBody, drawerBodyClassName)}>
          {children}
        </div>
      </AntDesignDrawer>
    </div>
  );
};

export default React.memo(MyDrawer, (prevProps, nextProps) => {
  return !prevProps.open && !nextProps.open;
});
